import { useId, useState } from 'react';

/**
 * The figures a record draws (proposal 0009, DECISIONS 24 Sep "Charts and
 * agent briefs"): bars, a stacked fit score against its threshold, a heat
 * grid of routes against criteria, and a SWOT. Each draws what it is
 * given and counts nothing a caller could type instead — an average is
 * computed from its scores here, never passed in — and each says in words
 * what it draws, so a screen reader reads the figure and not the shapes.
 * Ported from the package's record views (`bars`, `stack`, `heat`, `swot`).
 */

type BarTone = 'accent' | 'neutral' | 'danger';

const BAR_FILL: Record<BarTone, string> = { accent: 'bg-accent', neutral: 'bg-control', danger: 'bg-danger' };

export interface Bar {
  label: string;
  sub?: string | undefined;
  value: number;
  /** How the value reads, e.g. "€ 18.40 / kg"; the number itself when absent. */
  valueLabel?: string | undefined;
  tone?: BarTone | undefined;
  /** Drawn heavier: ours, the recommended one, the one the page is about. */
  strong?: boolean | undefined;
}

/** Horizontal bars on one scale: a label, the bar, its value. */
export function BarList({ bars, max, legend, label }: { bars: Bar[]; max?: number; legend?: string; label: string }) {
  const top = max ?? Math.max(0, ...bars.map((b) => b.value));
  return (
    <figure className="m-0 flex flex-col gap-2 border border-line px-4 py-3.5" aria-label={label}>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {bars.map((b) => {
          const pct = top > 0 ? Math.max(0, Math.min(100, (b.value / top) * 100)) : 0;
          return (
            <li key={b.label} className="grid grid-cols-[minmax(0,190px)_minmax(0,1fr)_minmax(0,96px)] items-center gap-3 phone:grid-cols-[minmax(0,1fr)_auto]">
              <span className="flex min-w-0 flex-col gap-px">
                <span className={`text-dense leading-tight ${b.strong ? 'font-semibold' : ''}`}>{b.label}</span>
                {b.sub && <span className="text-label text-meta">{b.sub}</span>}
              </span>
              <span aria-hidden className="relative block h-3 bg-sunken phone:order-last phone:col-span-2">
                <span className={`absolute inset-y-0 left-0 block ${BAR_FILL[b.tone ?? 'neutral']}`} style={{ width: `${pct}%` }} />
              </span>
              <span className={`text-right text-dense tabular-nums ${b.strong ? 'font-semibold' : ''}`}>{b.valueLabel ?? String(b.value)}</span>
            </li>
          );
        })}
      </ul>
      {legend && <figcaption className="border-t border-line pt-2 text-label text-meta">{legend}</figcaption>}
    </figure>
  );
}

export interface ScoreSegment {
  /** The rule that earned it, as the profile names it. */
  label: string;
  points: number;
}

const SEGMENT_FILL = ['bg-accent', 'bg-ink', 'bg-control', 'bg-warning', 'bg-secondary'];

/**
 * A fit score as the rules that earned it, stacked, against the profile's
 * threshold — the line a prospect must reach to be offered to a person.
 */
export function StackedScoreBar({ segments, threshold, max = 100 }: { segments: ScoreSegment[]; threshold: number; max?: number }) {
  const total = segments.reduce((n, s) => n + s.points, 0);
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / max) * 100))}%`;
  const reaches = total >= threshold;
  return (
    <figure className="m-0 flex flex-col gap-2 border border-line p-4" data-testid="stacked-score">
      <div
        role="img"
        aria-label={`Fit ${total} of ${max}, ${reaches ? 'at or over' : 'under'} the threshold of ${threshold}: ${segments.map((s) => `${s.label} ${s.points}`).join(', ') || 'no rule matched'}.`}
        className="relative flex h-5 bg-sunken"
      >
        {segments.map((s, i) => (
          <span key={s.label} title={`${s.label} · ${s.points}`} className={`block h-full flex-none border-r-2 border-page ${SEGMENT_FILL[i % SEGMENT_FILL.length]}`} style={{ width: pct(s.points) }} />
        ))}
        <span aria-hidden className="absolute -inset-y-[5px] block w-0.5 bg-ink" style={{ left: pct(threshold) }} />
      </div>
      <div aria-hidden className="flex justify-between text-micro tabular-nums text-meta">
        <span>0</span>
        <span>{Math.round(max / 2)}</span>
        <span>{max}</span>
      </div>
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-label text-secondary">
        <span className={`font-semibold ${reaches ? 'text-ink' : 'text-secondary'}`}>
          {total} · threshold {threshold}
        </span>
        {segments.map((s, i) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={`block h-2 w-2 ${SEGMENT_FILL[i % SEGMENT_FILL.length]}`} />
            {s.label} {s.points}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

export interface HeatCriterion {
  /** A few letters, for the column head. */
  short: string;
  /** What the column is, in full: its title and its reading. */
  full: string;
}

export interface HeatRow {
  label: string;
  /** One per criterion, in the head's order; null where it was not scored. */
  cells: Array<{ score: number | null; why?: string | null | undefined }>;
  note?: string | null | undefined;
}

function heatTone(score: number | null, top: number): string {
  if (score === null) return 'bg-raised text-meta';
  if (score >= top) return 'bg-accent text-accent-contrast';
  // One short of the top is the accent's tint (S:06): good, not best.
  if (score === top - 1) return 'bg-accent/35 text-ink';
  if (score === top - 2) return 'bg-sunken text-ink';
  return 'bg-raised text-meta';
}

/** The mean of what was scored; null when nothing was. */
export function heatAverage(row: HeatRow): number | null {
  const scored = row.cells.map((c) => c.score).filter((s): s is number => s !== null);
  return scored.length ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10 : null;
}

/**
 * Routes against criteria, each scored 1 to `top` where `top` is always
 * attractive. Each route's average stands beside its name, as a number and
 * a bar, and the routes are drawn highest first. A row opens to the reason
 * for every score, which is what makes a score worth trusting.
 */
export function HeatGrid({ head, rows, top = 5, legend, label }: { head: HeatCriterion[]; rows: HeatRow[]; top?: number; legend?: string; label: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const id = useId();
  const ranked = rows
    .map((r) => ({ row: r, avg: heatAverage(r) }))
    .sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1));
  const best = ranked[0]?.avg ?? null;
  const columns = { gridTemplateColumns: `minmax(170px,2.6fr) repeat(${head.length}, minmax(38px,1fr))` };
  return (
    <section className="overflow-x-auto border border-line" aria-label={label}>
      <div className="flex min-w-[580px] flex-col">
        <div className="grid items-end gap-[3px] px-3.5 pb-2 pt-3" style={columns} aria-hidden>
          <span className="text-micro uppercase tracking-label text-meta">Route · average of {top}</span>
          {head.map((h) => (
            <span key={h.short} title={h.full} className="min-w-0 cursor-help text-center text-micro uppercase leading-tight text-meta [overflow-wrap:break-word]">
              {h.short}
            </span>
          ))}
        </div>
        {ranked.map(({ row, avg }) => {
          const expanded = open === row.label;
          const leads = avg !== null && avg === best;
          const panel = `${id}-${row.label}`;
          return (
            <div key={row.label} className={`flex flex-col border-t border-line px-3.5 py-1.5 ${expanded ? 'bg-raised' : ''}`}>
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={panel}
                aria-label={`${row.label}: average ${avg ?? 'not scored'}. ${expanded ? 'Hide' : 'Show'} the reasons`}
                onClick={() => setOpen(expanded ? null : row.label)}
                className="grid min-h-tap cursor-pointer items-center gap-[3px] text-left"
                style={columns}
              >
                <span className="flex min-w-0 flex-col gap-1 pr-2.5">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-dense font-semibold">{row.label}</span>
                    <span className={`text-dense font-bold tabular-nums ${leads ? 'text-accent' : 'text-ink'}`}>{avg ?? '—'}</span>
                  </span>
                  <span aria-hidden className="relative block h-1.5 bg-sunken">
                    <span className={`absolute inset-y-0 left-0 block ${leads ? 'bg-accent' : 'bg-control'}`} style={{ width: `${avg === null ? 0 : (avg / top) * 100}%` }} />
                  </span>
                </span>
                {row.cells.map((c, i) => (
                  <span key={head[i]?.short ?? i} aria-hidden title={head[i] ? `${head[i].full}: ${c.score ?? 'not scored'}` : undefined} className={`flex h-9 items-center justify-center text-dense font-semibold tabular-nums ${heatTone(c.score, top)}`}>
                    {c.score ?? '—'}
                  </span>
                ))}
              </button>
              {expanded && (
                <dl id={panel} className="m-0 my-2 flex flex-col gap-px border border-line bg-line">
                  {row.cells.map((c, i) => (
                    <div key={head[i]?.short ?? i} className="grid grid-cols-[minmax(0,150px)_34px_minmax(0,1fr)] items-start gap-3 bg-page px-3 py-2">
                      <dt className="text-dense text-meta">{head[i]?.full ?? `Criterion ${i + 1}`}</dt>
                      <dd className={`m-0 py-0.5 text-center text-dense font-bold tabular-nums ${heatTone(c.score, top)}`}>{c.score ?? '—'}</dd>
                      <dd className="m-0 text-dense">{c.why ?? 'No reason recorded.'}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {row.note && <span className="pt-1 text-label text-meta">{row.note}</span>}
            </div>
          );
        })}
        {legend && <span className="border-t border-line px-3.5 py-2.5 text-label text-meta">{legend}</span>}
      </div>
    </section>
  );
}

export type SwotQuadrant = 'Strengths' | 'Weaknesses' | 'Opportunities' | 'Threats';

const SWOT_EDGE: Record<SwotQuadrant, string> = {
  Strengths: 'border-t-accent',
  Weaknesses: 'border-t-warning',
  Opportunities: 'border-t-strong',
  Threats: 'border-t-danger',
};

/** A SWOT whose every point names the criterion it rests on (ADR-0132). */
export function SwotGrid({ quadrants }: { quadrants: Array<{ label: SwotQuadrant; items: Array<{ text: string; from: string }> }> }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-px border border-line bg-line">
      {quadrants.map((q) => (
        <section key={q.label} aria-label={q.label} className={`flex flex-col gap-2.5 border-t-[3px] bg-page p-4 ${SWOT_EDGE[q.label]}`}>
          <span className="text-label font-semibold uppercase tracking-label">{q.label}</span>
          {q.items.length === 0 && <span className="text-dense text-meta">None recorded.</span>}
          {q.items.map((it) => (
            <span key={it.text} className="flex flex-col gap-0.5">
              <span className="text-dense">{it.text}</span>
              <span className="text-label text-meta">From: {it.from}</span>
            </span>
          ))}
        </section>
      ))}
    </div>
  );
}
